import api from "./api";

const profileService = {
  // Profile management
  getProfile: () => api.get("/profile"),

  // Get compact contact information
  getProfileContact: () => api.get("/profile/contact"),

  updateProfile: (profileData) => {
    // For super admin, only use the admin endpoint
    if (profileData.role && profileData.role.toLowerCase() === 'superadmin') {
      // Only include the fields that the admin endpoint accepts
      const { firstName, lastName, phoneNumber, gender, dob } = profileData;
      // Normalize phone number to digits only (remove spaces, +, dashes)
      const normalizedPhone = phoneNumber ? String(phoneNumber).replace(/\D/g, "") : phoneNumber;
      return api.put("/web/profile/update-admin-details", {
        firstName,
        lastName,
        phoneNumber: normalizedPhone,
        gender,
        dob
      });
    }
    // For other roles, use the institute endpoint
    return api.put("/web/profile/update-institute-details", profileData);
  },

  // Update teacher/HOD profile details
  updateTeacherProfile: (profileData) => {
    const {
      firstName,
      lastName,
      phoneNumber,
      gender,
      dob,
      qualification,
      experienceYears,
      joiningDate,
      endingDate,
      designation,
      departmentId,
      departmentName,
      salary,
      street,
      city,
      state,
      country,
      pinCode,
      address,
      fcmToken,
      teacherLoginID,
      fullName,
      role,
      instituteName,
      instituteLogo,
      profileImage,
      id,
      userId,
    } = profileData;
    const normalizedPhone = phoneNumber ? String(phoneNumber).replace(/\D/g, "") : phoneNumber;
    return api.put("/mobile/profile/update-teacher-details", {
      id,
      userId,
      teacherLoginID,
      firstName,
      lastName,
      fullName,
      phoneNumber: normalizedPhone,
      gender,
      dob,
      qualification,
      experienceYears,
      joiningDate,
      endingDate,
      designation,
      departmentId,
      departmentName,
      salary,
      street,
      city,
      state,
      country,
      pinCode,
      address,
      fcmToken,
      role,
      instituteName,
      instituteLogo,
      profileImage,
    });
  },

  // Request permission to edit teacher/HOD profile
  requestToEditProfile: () => api.post("/teacher/request-to-edit"),

  uploadAvatar: (formData) =>
    api.post("/profile/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Update profile image for admin/institute roles
  updateProfileImage: (formData) =>
    api.put("/web/profile/profile-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Update profile image for teacher/HOD roles
  updateTeacherProfileImage: (formData) =>
    api.put("/mobile/profile/profile-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  deleteWebProfileImage: () => api.delete("/web/profile/profile-image"),

  deleteTeacherProfileImage: () => api.delete("/mobile/profile/profile-image"),

  // Contact information requests
  getContactInfoRequests: (params) =>
    api.get("/profile/contact-requests", { params }),

  // Profile update requests listings
  getStaffProfileRequests: (params) =>
    api.get("/institute/all-requested-teachers-listing", { params }),

  getStudentProfileRequests: (params) =>
    api.get("/student/get-all-profile-requests", { params }),

  updateTeacherRequestStatus: (payload) =>
    api.put("/institute/teacher-request-update", payload),

  updateStudentRequestStatus: (payload) =>
    api.put("/student/student-request-update", payload),

  getContactInfoRequestById: (id) => api.get(`/profile/contact-requests/${id}`),

  createContactInfoRequest: (requestData) =>
    api.post("/profile/contact-requests", requestData),

  // Institute contact update request (POST /institute/contact-update-request)
  createInstituteContactUpdateRequest: (payload) =>
    api.post("/institute/contact-update-request", payload),

  // Get contact update requests list (GET /institute/contact-update-requests)
  getInstituteContactUpdateRequests: (params) =>
    api.get("/institute/contact-update-requests", { params }),

  // Review contact update request (PUT /institute/contact-update-request/:id/review)
  reviewInstituteContactUpdateRequest: (id, payload) =>
    api.put(`/institute/contact-update-request/${id}/review`, payload),

  // Approve/Reject contact info requests
  approveContactInfoRequest: (id) =>
    api.post(`/profile/contact-requests/${id}/approve`),

  rejectContactInfoRequest: (id, reason) =>
    api.post(`/profile/contact-requests/${id}/reject`, { reason }),

  // Get requests by status
  getPendingContactRequests: () =>
    api.get("/profile/contact-requests", { params: { status: "pending" } }),

  // My requests
  getMyContactRequests: () => api.get("/profile/contact-requests/my"),

  // View other profiles (with permission)
  getUserProfile: (userId) => api.get(`/profile/user/${userId}`),

  // For admin/superadmin - manage any user profile
  updateUserProfile: (userId, profileData) =>
    api.put(`/profile/user/${userId}`, profileData),

  // Emergency contact
  updateEmergencyContact: (data) => api.put("/profile/emergency-contact", data),

  // Password change
  changePassword: (data) => api.post("/profile/change-password", data),

  // Preferences
  getPreferences: () => api.get("/profile/preferences"),

  updatePreferences: (preferences) =>
    api.put("/profile/preferences", preferences),

  // Institute details
  getInstituteDetails: () => api.get("/web/profile/institute-details"),

  // Super admin details
  getSuperAdminDetails: () => api.get("/web/profile/admin-details"),

  updateInstituteDetails: (data) => {
    const normalizedPhone = data?.phoneNumber ? String(data.phoneNumber).replace(/\D/g, "") : data?.phoneNumber;
    const payload = { ...data, phoneNumber: normalizedPhone };
    return api.put("/web/profile/update-institute-details", payload);
  },

  // Notification settings
  getNotificationSettings: () => api.get("/profile/notification-settings"),
  updateNotificationSettings: (settings) =>
    api.put("/profile/notification-settings", settings),

  getTeacherUpdatedProfile: () => api.get("/mobile/profile/get-teacher-details"),

  // FCM token for push notifications (v1 profile)
  updateFcmToken: (fcmToken) => api.put("/profile", { fcmToken }),
};

export default profileService;